// ============================================================================
// LA COLONNE DE NAVIGATION D'UN ÉCRAN À LONGUE LISTE — Paramètres et Référentiels.
//
// UTILITÉ : au-delà de cinq ou six entrées, la barre d'onglets soulignés ne tient plus (règle de la charte) ; l'écran
// range alors ses entrées dans une carte collante de 262 px, recherche en tête, entrée active en vert. Les deux écrans la
// DESSINENT à l'identique : écrite deux fois, elle divergerait au premier réglage.
//
// ⚠️ Mesures relevées du DOM de la maquette (`isSet`, `isFamilles`) : carte 262 px, champ de 36 px à loupe GAUCHE
//    (rembourrage 34), entrées `9px 11px` à écart 11, titres de groupe en capitales mono de 10 px, pastille de 20 px.
// ⚠️ La colonne ne cherche rien elle-même : chaque écran sait ce que sa recherche fouille (des réglages, des valeurs) et
//    lui remet des groupes déjà filtrés, avec la valeur trouvée à montrer sous l'entrée.
// ============================================================================
import type { ReactNode } from 'react';
import { Icon } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';

/** Une entrée de la colonne, prête à rendre (libellés déjà traduits). */
export interface SideNavItem {
  key: string;
  label: string;
  icon: IconName;
  /**
   * La pastille à droite : un compte (gris), ou ce qui reste à traiter (ambre, `alert`). `title` dit les deux en clair.
   * `null` : pas de pastille — une liste sans compte ne montre pas de zéro inventé.
   */
  badge?: { text: string; alert: boolean; title: string } | null;
  /** La valeur que la recherche a trouvée dans cette entrée (« « Robes » +2 »), montrée sous son nom. */
  hit?: string | null;
  /** Le cadenas : l'entrée existe, mais tout n'y est pas modifiable — dit AVANT d'ouvrir. */
  locked?: boolean;
}

/** Un groupe d'entrées ; `title: null` pour une colonne sans titres. */
export interface SideNavGroup {
  title: string | null;
  items: SideNavItem[];
}

/** La colonne : recherche, groupes et entrées. */
export function SideNav({ query, onQuery, placeholder, groups, active, onSelect, empty, label }: {
  query: string;
  onQuery: (q: string) => void;
  placeholder: string;
  groups: readonly SideNavGroup[];
  /** L'entrée ouverte — `null` pendant une recherche, où aucune entrée n'est « la page ». */
  active: string | null;
  onSelect: (key: string) => void;
  /** Ce que la colonne dit quand la recherche ne laisse aucune entrée. */
  empty: ReactNode;
  /** Le nom de la navigation, pour qui ne voit pas la colonne. */
  label: string;
}) {
  const shown = groups.filter((g) => g.items.length > 0);
  return (
    <nav aria-label={label} style={{ width: 262, flex: '0 0 262px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden', position: 'sticky', top: 0 }}>
      <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--ink-100)' }}>
        <span style={{ position: 'relative', display: 'block' }}>
          <input value={query} onChange={(e) => onQuery(e.target.value)} placeholder={placeholder}
            style={{ width: '100%', height: 36, padding: '0 12px 0 34px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-lead)', color: 'var(--ink-900)', boxSizing: 'border-box', outline: 'none' }} />
          {/* La loupe est À GAUCHE dans le dessin — c'est elle qui creuse le rembourrage de 34. */}
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-400)', display: 'flex' }}>
            <Icon name="search" size={14} stroke={2} />
          </span>
        </span>
      </div>
      <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
        {shown.map((g, gi) => (
          <div key={g.title ?? `group-${gi}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {g.title !== null && (
              <div style={{ padding: gi ? '12px 11px 4px' : '4px 11px 4px', fontFamily: 'var(--font-mono)', fontSize: '10px', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>
                {g.title}
              </div>
            )}
            {g.items.map((item) => <SideNavButton key={item.key} item={item} active={item.key === active} onSelect={onSelect} />)}
          </div>
        ))}
        {shown.length === 0 && (
          <div style={{ padding: '14px 12px', textAlign: 'center', fontSize: '12.5px', color: 'var(--ink-400)' }}>{empty}</div>
        )}
      </div>
    </nav>
  );
}

/**
 * Une entrée. ⚠️ Son fond vit dans la FEUILLE (`oz-side-nav-item`) et non en ligne : le survol est un `:hover`, et un fond
 * posé en ligne l'emporterait toujours sur lui.
 */
function SideNavButton({ item, active, onSelect }: { item: SideNavItem; active: boolean; onSelect: (key: string) => void }) {
  const badge = item.badge ?? null;
  return (
    <button type="button" className="oz-side-nav-item" aria-current={active ? 'page' : undefined} onClick={() => onSelect(item.key)}
      style={{
        display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '9px 11px', border: 'none',
        borderRadius: 'var(--r-ctrl)', textAlign: 'left', cursor: 'pointer',
        color: active ? 'var(--teal-800)' : 'var(--ink-700)', fontSize: 'var(--t-title)', fontWeight: active ? 600 : 450,
      }}>
      <Icon name={item.icon} size={17} stroke={1.8} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span>{item.label}</span>
        {item.hit && (
          <span style={{ fontSize: '11.5px', fontWeight: 450, color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.hit}</span>
        )}
      </span>
      {item.locked && <Icon name="lock" size={12} stroke={2.2} color="var(--ink-400)" />}
      {badge && (
        <span title={badge.title} style={{
          minWidth: 22, height: 20, padding: '0 6px', borderRadius: 'var(--r-pill)', boxSizing: 'border-box', flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
          background: badge.alert ? 'var(--amber-bg)' : active ? 'color-mix(in oklab, var(--teal-500) 18%, transparent)' : 'var(--ink-100)',
          color: badge.alert ? 'var(--amber-fg)' : active ? 'var(--teal-800)' : 'var(--ink-600)',
        }}>{badge.text}</span>
      )}
    </button>
  );
}
